<template>
  <div>
    <!-- Search Container Card -->
    <n-search-container
      v-if="searchFields.length"
      :active="true"
      :show-header="false"
      show-search
      :loading="loading"
      @search="$emit('search', $event)"
      @reset="$emit('reset')"
    >
      <n-search-input
        ref="search"
        v-model="innerParams"
        :fields="searchFields"
      >
        <template #typeId="item">
          <b-form-group
            :label-for="item.field.key"
            :label="$t(item.field.label)"
          >
            <b-form-radio-group
              v-if="item.field.type === 'radio'"
              :id="item.field.key"
              :ref="item.field.key"
              v-model="params[item.field.key]"
              buttons
              button-variant="outline-primary rounded-0"
              class="flex-wrap"
            >
              <b-form-radio
                v-for="option in item.field.options"
                :key="option.value"
                :value="option.value"
              >
                {{ $t(option.text) }}
              </b-form-radio>
            </b-form-radio-group>
          </b-form-group>
        </template>

        <template #period="item">
          <b-form-group
            :label-for="item.field.key"
            :label="$t(item.field.label)"
          >
            <b-form-radio-group
              v-if="item.field.type === 'radio'"
              :id="item.field.key"
              :ref="item.field.key"
              v-model="params[item.field.key]"
              buttons
              button-variant="outline-primary rounded-0"
              class="flex-wrap"
            >
              <b-form-radio
                v-for="option in item.field.options"
                :key="option.value"
                :value="option.value"
              >
                {{ $t(option.text) }}
              </b-form-radio>
            </b-form-radio-group>
          </b-form-group>
        </template>

        <template
          v-for="slotName in searchSlotNames"
          #[slotName]="scope"
        >
          <slot
            :name="slotName"
            v-bind="scope"
          />
        </template>
      </n-search-input>
    </n-search-container>

    <!-- Table Container Card -->
    <b-card
      no-body
      class="mb-0"
    >
      <div class="m-0-5">
        <b-row
          v-if="showBackButton || showRefreshButton"
          class="mb-2"
        >
          <b-col
            v-if="showBackButton"
            cols="6"
          >
            <b-button
              type="button"
              variant="outline-secondary"
              :disabled="loading"
              @click="$emit('back')"
            >
              <feather-icon icon="ChevronLeftIcon" />
              {{ $t('button.back') }}
            </b-button>
          </b-col>
          <b-col
            v-if="showRefreshButton"
            :cols="showBackButton ? 6 : 12"
            class="text-right"
          >
            <b-button
              type="button"
              variant="outline-secondary"
              :disabled="loading"
              @click="$emit('refresh')"
            >
              <feather-icon icon="RefreshCcwIcon" />
              {{ $t('button.refresh') }}
            </b-button>
          </b-col>
        </b-row>
        <b-row>
          <b-col>
            <label>{{ $t('general.entries') }}</label>
            <v-select
              :value="perPage"
              :dir="isRTL ? 'rtl' : 'ltr'"
              :options="perPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block ml-50 mr-1"
              @input="$emit('update:perPage', $event)"
            />
          </b-col>
        </b-row>
      </div>

      <div>
        <n-table
          :fields="fields"
          :items="items"
          :busy="loading"
          :current-page="params.page"
          :per-page="perPage"
          :total="total"
          @sort-changed="(...args) => $emit('sort-changed', ...args)"
        >
          <template #cell(isVendor)="{ item }">
            <b-badge
              v-if="item.isVendor"
              pill
              variant="info"
              badge-glow
            >
              {{ $t('general.external') }}
            </b-badge>
            <b-badge
              v-else
              pill
              variant="primary"
              badge-glow
            >
              {{ $t('general.internal') }}
            </b-badge>
          </template>

          <template #cell(username)="{ item }">
            <b-link
              v-if="typeId !== 5 && user.typeId !== 5"
              class="link-underline"
              @click="viewMember(item.userId)"
            >
              {{ item.username }}
            </b-link>
            <span v-else>{{ item.username }}</span>
            <small
              v-if="item.secondUsername"
              class="d-block text-wala"
            >
              ({{ item.secondUsername }})
            </small>
          </template>

          <template #cell(commissionBalance)="{ item }">
            <span>{{ item.commissionBalance | currency(item.ccy) }}</span>
          </template>

          <template #cell(receiveCommission)="{ item }">
            <b-link
              class="link-underline"
              @click="openPaybackCommissionHistory(item)"
            >
              {{ item.receiveCommission | currency(item.ccy) }}
            </b-link>
          </template>

          <template #cell(claimCommission)="{ item }">
            <span>{{ item.claimCommission | currency(item.ccy) }}</span>
          </template>

          <template #cell(balance)="{ item }">
            <span>{{ item.balance | currency(item.ccy) }}</span>
          </template>

          <template #cell(member)="{ item }">
            <b-link
              v-if="typeId !== 5 && user.typeId !== 5"
              class="link-underline"
              @click="viewMember(item.userId)"
            >
              {{ item.member }}
            </b-link>
            <span v-else>{{ item.member }}</span>
          </template>

          <template
            v-if="headerLabel"
            #thead-top=""
          >
            <b-tr class="text-center">
              <b-th
                :key="fields.length"
                :colspan="fields.length"
              >
                {{ headerLabel }}
              </b-th>
            </b-tr>
          </template>

          <!-- Forward any extra cell(...) slots supplied by the parent -->
          <template
            v-for="slotName in tableSlotNames"
            #[slotName]="scope"
          >
            <slot
              :name="slotName"
              v-bind="scope"
            />
          </template>

          <template #custom-foot>
            <b-tr v-if="!loading">
              <b-th colspan="3" />
              <b-th class="text-right">
                <h5 class="font-weight-bold">
                  {{ totalReport.commissionBalance | currency(totalReport.ccy) }}
                </h5>
              </b-th>
              <b-th class="text-right">
                <h5 class="font-weight-bold">
                  {{ totalReport.receiveCommission | currency(totalReport.ccy) }}
                </h5>
              </b-th>
              <b-th class="text-right">
                <h5 class="font-weight-bold">
                  {{ totalReport.claimCommission | currency(totalReport.ccy) }}
                </h5>
              </b-th>
              <b-th />
              <b-th
                v-if="typeId !== 5 && user.typeId !== 5"
                class="text-center font-weight-bold"
              >
                <h5>{{ totalReport.member }}</h5>
              </b-th>
            </b-tr>
          </template>
        </n-table>

        <n-pagination
          ref="pagination"
          class="mt-2"
          :total="total"
          :per-page="perPage"
          :page="params.page"
          @change="$emit('page-change', $event)"
        />
      </div>
    </b-card>

    <payback-commission-history ref="paybackCommissionHistoryModal" />
  </div>
</template>

<script>
import {
  BBadge,
  BButton,
  BCard,
  BCol,
  BFormGroup,
  BFormRadio,
  BFormRadioGroup,
  BLink,
  BRow,
  BTh,
  BTr,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import NPagination from '@/components/NPagination.vue'
import NSearchContainer from '@/components/NSearchContainer.vue'
import NSearchInput from '@/components/NSearchInput.vue'
import NTable from '@/components/NTable.vue'
import PaybackCommissionHistory from '@/views/report/paybackCommissionReport/paybackCommissionHistory/List.vue'

export default {
  name: 'PaybackCommissionReport',
  components: {
    PaybackCommissionHistory,
    BLink,
    BBadge,
    BFormRadioGroup,
    BFormRadio,
    BFormGroup,
    BCard,
    BRow,
    BCol,
    BButton,
    BTr,
    BTh,
    NPagination,
    NSearchContainer,
    NSearchInput,
    NTable,
    vSelect,
  },
  props: {
    fields: {
      type: Array,
      required: true,
    },
    searchFields: {
      type: Array,
      default: () => [],
    },
    items: {
      type: Array,
      default: () => [],
    },
    totalReport: {
      type: Object,
      default: () => {},
    },
    params: {
      type: Object,
      required: true,
    },
    user: {
      type: Object,
      default: () => {},
    },
    loading: Boolean,
    total: {
      type: Number,
      default: 0,
    },
    perPage: {
      type: [Number, String],
      required: true,
    },
    perPageOptions: {
      type: Array,
      default: () => [10, 25, 50, 100],
    },
    isRTL: Boolean,
    headerLabel: {
      type: String,
      default: '',
    },
    showBackButton: Boolean,
    showRefreshButton: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    // v-model passthrough into <n-search-input>, keeps parent's `params`
    // object reactive without this component needing to know its shape.
    innerParams: {
      get() {
        return this.params
      },
      set(value) {
        this.$emit('update:params', value)
      },
    },
    // Any scoped slot the parent passes named "typeId", "period", etc.
    // (i.e. matching a search field key) goes to n-search-input.
    searchSlotNames() {
      const searchKeys = this.searchFields.map(f => f.key)
      return Object.keys(this.$scopedSlots)
        .filter(name => searchKeys.includes(name))
    },
    // Everything else (extra cell(xxx) overrides, etc.) forwarded to n-table.
    tableSlotNames() {
      const searchKeys = this.searchFields.map(f => f.key)
      return Object.keys(this.$scopedSlots)
        .filter(name => !searchKeys.includes(name))
    },
    userData() {
      return this.$store.state.profile.name
        ? this.$store.state.profile
        : JSON.parse(localStorage.getItem('userData'))
    },
    typeId() {
      if (this.$store.state.profile.typeId) {
        return this.$store.state.profile.typeId
      }
      return this.userData?.typeId
    },
  },
  methods: {
    viewMember(userId) {
      this.$emit('viewMember', userId)
    },
    openPaybackCommissionHistory(item) {
      this.$refs.paybackCommissionHistoryModal.show({
        page: 1,
        order: null,
        sort: null,
        userId: item.userId,
        startDate: this.params.startDate,
        toDate: this.params.toDate,
        period: this.params.period,
      })
    },
  },
}
</script>
